import { NextResponse } from 'next/server';

import { transactions } from '@prisma/client';

import { Session } from 'next-auth';

import {
  UserKYCLevel,
  Currency,
  Gateway,
  TransactionStates,
  // TransactionType,
  TransactionTypes,
  WithdrawalMethod,
} from '@/enums/core';

import { findDepositWallet } from '@/features/deposit/utils';

import { errors } from '@/localization';

import {
  checkNumber,
  decrypt,
  exception,
  extractNumberOfFakeUuid,
  fakeUuid,
} from '@/utils/helpers';

import { prisma } from '@/utils/prisma';

import { MAX_WITHDRAWAL_AMOUNT } from '@/constants/core';

import {
  QueryResponse, WithdrawalCash, Withdrawal, WithdrawalProps,
  Account,
} from '../interface';

export const banks = ['Banco Atlantida S.A', 'Banco Occidente S.A',
  'Banco de los Trabajadores', 'Banco Financiero Centro Americana S.A (FICENSA)',
  'Banco Hondureño del Café S.S (BANHCAFÉ)', 'Banco del País S.A',
  'Banco Financiera Comercial Hondureña S.A (FICOHSA)', 'Banco Lafise Honduras S.A',
  'Banco Davivienda Honduras S.A', 'Banco Promérica S.A', 'Banco de Desarrollo Rural Honduras S.A',
  'Banco Azteca de Honduras S.A', 'Banco Popular S.A', 'Banco de America Central Honduras S.A (Bac I Honduras)',
  'Banco de Honduras (Citi Honduras)', 'Banco Nacional de Desarrollo Agrícola', 'Banco Hondureño para la Producción y la Vivienda (BANHPROVI)'];

export const countApprovedWithdrawalsToday = async (userid: number): Promise<QueryResponse> => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const count = await prisma.transactions.findMany({
      where: {
        user_id: userid,
        status: TransactionStates.APPROVED,
        type: TransactionTypes.WITHDRAWL,
        created_at: {
          gt: today,
        },
      },
    });
    return { status: true, data: count };
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: countApprovedWithdrawalsToday', method: 'GET', req: userid });
    return { status: false, data: null };
  }
};

export const findWalletByUser = async ({ userid, address }:{ userid: number, address: string }): Promise<QueryResponse> => {
  try {
    const wallet = await prisma.wallets.findMany({
      where: {
        user_id: userid,
        address,
        status: true,
      },
    });
    if (wallet && wallet.length > 0) {
      return { status: true, data: wallet };
    }
    return { status: false, data: null };
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: findWalletByUserAndStatus', method: 'GET', req: { userid, address } });
    return { status: false, data: null };
  }
};

export const findTransactionByUserAndStatus = async (userid: number, type: TransactionTypes): Promise<QueryResponse> => {
  try {
    const transaction = await prisma.transactions.findMany({
      where: {
        user_id: userid,
        type,
        status: {
          in: [TransactionStates.PENDING, TransactionStates.ISSUED, TransactionStates.APPROVED],
        },
      },
    });

    if (transaction && transaction.length > 0) {
      return { status: true, data: transaction };
    }
    return { status: false, data: null };
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: findTransactionByUserAndStatus', method: 'GET', req: { userid, type } });
    return { status: false, data: null };
  }
};

export const withdrawalCash = async (
  {
    user_id,
    status,
    type,
    amount,
    gateway,
    description,
  }: WithdrawalCash,
): Promise<QueryResponse> => {
  try {
    if (amount > 0) {
      const txn = fakeUuid(new Date().getTime().toString()).substring(0, 15);
      const newTransaction: transactions = await prisma.transactions.create({
        data: {
          user_id,
          status,
          type,
          amount,
          gateway,
          description,
          currency: Currency.HNL,
          id_txn: txn,
          deposit_id: txn,
        },
      });
      // await fetchXtremePushTransaction(TransactionType.WITHDRAWAL, newTransaction);
      return { status: true, data: newTransaction };
    }
    return { status: false, data: null };
  } catch (error) {
    await exception(error, {
      route: '/features/dashboard/utils/index.ts: withdrawalCash',
      method: 'POST',
      req: {
        user_id, status, type, amount, gateway, description,
      },
    });
    return { status: false, data: null };
  }
};

export const withdrawalWallet = async (
  {
    address,
    status,
    type,
    amount,
    currency,
    gateway,
    id_withdrawal,
  }: Withdrawal,
): Promise<QueryResponse> => {
  // find if the transaction already exists if so then update the status
  const findTransaction = await prisma.transactions.updateMany({
    where: {
      id: Number(id_withdrawal),
    },
    data: {
      status,
      amount,
    },
  });

  if (findTransaction.count > 0) {
    return { status: true, data: findTransaction };
  }
  // if the transaction does not exist then create a new one

  // find the wallet associated with the address
  const { status: walletStatus, data: wallet } = await findDepositWallet(address);

  if (!walletStatus) {
    return { status: false, data: null };
  }

  const { user_id: userId } = wallet;

  const newTransaction = await prisma.transactions.create({
    data: {
      user_id: userId,
      status,
      type,
      amount,
      currency,
      gateway,
      withdrawal_id: id_withdrawal,
    },
  });

  return { status: true, data: newTransaction };
};

export const forbiddenResponse = (message: string) => {
  return new NextResponse(JSON.stringify({ errorData: message }), { status: 403 });
};

export const isValidSession = (session: Session | null): boolean => {
  return !!session && !!session.user && !!session.user.data && !!session.user.tag;
};

export const unauthorizedResponse = () => {
  return NextResponse.json({ message: errors.unauthorized }, { status: 401 });
};

export const getUserId = async (session: Session): Promise<number | null> => {
  if (session?.user?.data && session?.user?.tag) {
    return Number(await decrypt(session.user.data, session.user.tag));
  }
  return null;
};

export const getTotalApprovedWithdrawals = async (userId: number): Promise<number> => {
  const { status, data: approvedWithdrawalsToday } = await countApprovedWithdrawalsToday(userId);
  return status && approvedWithdrawalsToday ? approvedWithdrawalsToday.reduce((acc: number, withdrawal: transactions) => { return acc + Number(withdrawal?.amount || 0); }, 0) : 0;
};

export const isWithdrawalLimitReached = (totalApprovedWithdrawals: number, kycLevel: number, amount: number): boolean => {
  let limit = 0;
  if (kycLevel === 1) {
    limit = MAX_WITHDRAWAL_AMOUNT;
  } else if (kycLevel === 2) {
    limit = MAX_WITHDRAWAL_AMOUNT;
  }
  return totalApprovedWithdrawals + Number(amount) > limit;
};

export const handleCashWithdrawal = async (userId: number, amount: number) => {
  try {
    const { status: statusQuery, data: transaction } = await withdrawalCash({
      user_id: userId,
      status: TransactionStates.ISSUED,
      type: TransactionTypes.WITHDRAWL,
      amount: Number(amount),
      gateway: Gateway.CASH,
      description: 'retiro en efectivo',
    });
    if (statusQuery) {
      return { transactionId: transaction.id };
    }
    await exception(errors.requestFailed, { route: '/features/dashboard/utils/index.ts: handleCashWithdrawal', method: 'POST', req: { userId, amount } });
    return { errorData: errors.requestFailed };
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: handleCashWithdrawal', method: 'POST', req: { userId, amount } });
    return { errorData: errors.requestFailed };
  }
};

export const handleBankWithdrawal = async (userId: number, props: WithdrawalProps) => {
  try {
    if ((!props.address.network || !props.address.bank || !props.address.address)) {
      return { transaccionId: null, errorData: 'Datos bancarios no ingresados' };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const withdrawalsToday = await prisma.transactions.count({
      where: {
        user_id: userId,
        status: TransactionStates.ISSUED,
        created_at: {
          gte: today,
        },
        type: TransactionTypes.WITHDRAWL,
      },
    });

    if (withdrawalsToday >= 2) {
      return { transaccionId: null, errorData: 'Límite de retiros bancarios alcanzado para hoy.' };
    }

    const description = `Cuenta Bancaria: n. ${props.address.network}, Banco:${props.address.bank}, Tipo: ${props.address.address}, DNI: ${props.note}`;
    const isActiveAccount = await prisma.wallets.findFirst({
      where: {
        user_id: userId,
        address: `${props.address.bank}-${props.address.address}-${props.address.network}`,
      },
      select: {
        status: true,
      },
    });
    if (!isActiveAccount?.status) {
      return { transaccionId: null, errorData: 'Cuenta bancaria inactiva' };
    }
    const { status: statusQuery, data: transaction } = await withdrawalCash({
      user_id: userId,
      status: TransactionStates.ISSUED,
      type: TransactionTypes.WITHDRAWL,
      amount: Number(props.amount),
      gateway: Gateway.BANK,
      description,
    });

    if (statusQuery) {
      return { transaccionId: transaction.id, errorData: null };
    }
    await exception(errors.requestFailed, { route: '/features/dashboard/utils/index.ts: handleBankWithdrawal', method: 'POST', req: { userId, props } });
    return { transaccionId: null, errorData: errors.requestFailed };
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: handleBankWithdrawal', method: 'POST', req: { userId, props } });
    return { transaccionId: null, errorData: errors.requestFailed };
  }
};

export const handleCryptoWithdrawal = async (userId: number, props: WithdrawalProps) => {
  const options = {
    amount: props.amount,
    currency: props.currency,
    crypto: props.crypto,
    address: props.address,
    note: props.note,
  };

  const withdrawalOptions = Object.entries(options).reduce((acc: any, [key, value]) => {
    if (value !== undefined) acc[key] = value;
    return acc;
  }, {});
  // eslint-disable-next-line no-console
  console.log('🚀 ~ withdrawalOptions ~ withdrawalOptions:', withdrawalOptions);

  try {
    // const res = await client.createWithdrawal(withdrawalOptions);
    // if (res.id) {
    //   await withdrawalWallet({
    //     user_id: userId,
    //     description: '',
    //     address: props.address.address,
    //     status: props.status,
    //     type: TransactionTypes.WITHDRAWL,
    //     amount: props.amount,
    //     currency: Currency.HNL,
    //     gateway: Gateway.COINPAYMENTS,
    //     id_withdrawal: res.id,
    //   });
    //   return { transactionId: res.id };
    // }
    await exception({ errorData: 'No se pudo crear la transacción' }, { route: '/features/dashboard/utils/index.ts: handleCryptoWithdrawal', method: 'POST', req: { userId, props } });
    return { transactionId: null };
  } catch (error: any) {
    const errorData = error.extra.data.error;
    return { errorData };
  }
};

export const processWithdrawal = async (userId: number, props: WithdrawalProps) => {
  const { amount } = props;
  switch (props.method) {
    case WithdrawalMethod.CASH: {
      const { transactionId } = await handleCashWithdrawal(userId, amount);
      return { data: transactionId };
    }
    case WithdrawalMethod.BANK: {
      const { transaccionId, errorData } = await handleBankWithdrawal(userId, props);
      return { data: transaccionId, errorData };
    }
    case WithdrawalMethod.CRYPTO: {
      const { transactionId } = await handleCryptoWithdrawal(userId, props);
      return { data: transactionId };
    }
    default:
      return { errorData: errors.requestFailed };
  }
};

export const createAccount = async (userId: number, accounts: Account): Promise<{ status: boolean, message: string, data: Account[] }> => {
  try {
    const addressAccount = `${accounts.bank}-${accounts.type}-${accounts.number}`;
    const isAccount = await prisma.wallets.findFirst({
      where: {
        address: addressAccount,
      },
    });
    if (!isAccount) {
      const newAccount = await prisma.wallets.create({
        data: {
          user_id: userId, address: addressAccount, currency: 'HNL', bank: accounts.bank, status: true, method: 1,
        },
      });

      const address = newAccount.address?.split('-');
      if (!address) {
        return {
          status: false,
          message: 'Error al crear la cuenta',
          data: [],
        };
      }
      const newAccountUser: Account = {
        token: fakeUuid(newAccount.id.toString()),
        bank: address[0],
        type: address[1],
        number: address[2],
      };
      return {
        status: true,
        message: 'Cuenta creada correctamente',
        data: [newAccountUser],
      };
    }
    return {
      status: false,
      message: 'Cuenta ya existe, si consideras que es un error contacta con soporte',
      data: [],
    };
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: createAccount', method: 'POST', req: { userId, accounts } });
    return {
      status: false,
      message: 'Error al crear la cuenta, si consideras que es un error contacta con soporte',
      data: [],
    };
  }
};

export const getAccount = async (userId: number): Promise<Account[]> => {
  try {
    const accounts = await prisma.wallets.findMany({
      where: {
        user_id: userId,
      },
    });

    const userAccounts: Account[] = accounts.map((account) => {
      const address = account.address?.split('-');
      if (!address) {
        return {} as Account;
      }

      const userAccount: Account = {
        token: fakeUuid(account.id.toString()),
        bank: address[0],
        type: address[1],
        number: address[2],
      };
      return userAccount;
    });
    return userAccounts;
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: getAccount', method: 'GET', req: userId });
    return [] as Account[];
  }
};

export const deleteAccount = async (token: string, userId: number) => {
  try {
    const id = extractNumberOfFakeUuid(token);

    if (!checkNumber(id)) {
      await exception(errors.requestFailed, { route: '/features/dashboard/utils/index.ts: deleteAccount', method: 'DELETE', req: token });
      return [];
    }

    const deletedAccount = await prisma.wallets.update({
      where: {
        id,
        user_id: userId,
      },
      data: {
        status: false,
      },
    });
    return deletedAccount;
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: deleteAccount', method: 'DELETE', req: token });
    return [];
  }
};

// export const sendEmailCancelWithdrawl = async (transaction: transactions) => {
//   try {
//     const user = await prisma.users.findFirst({
//       where: {
//         id: transaction.user_id,
//       },
//       select: {
//         email: true,
//         name: true,
//       },
//     });
//     if (!user) {
//       return { errorData: errors.requestFailed };
//     }
//     const templateId = process.env.PRIVATE_SENDGRID_CANCEL_WITHDRAWAL_TEMPLATE_ID as string;
//     const message = {
//       to: user?.email,
//       from: process.env.PRIVATE_SENDGRID_EMAIL as string,
//       templateId,
//       dynamicTemplateData: {
//         firstname: user?.name,
//         id: transaction.id,
//         description: transaction.description,
//       },
//       hideWarnings: true,
//     };
//     sgMail.setApiKey(process.env.PRIVATE_SENDGRID_API_KEY as string);
//     await sgMail.send(message);
//     return { message: 'Email sent' };
//   } catch (error) {
//     await exception(error, { route: '/features/dashboard/utils/index.ts: sendEmailCancelWithdrawl', method: 'POST', req: transaction });
//     return { errorData: errors.requestFailed };
//   }
// };

export const cancelWithdrawl = async (transactionId: number) => {
  try {
    const transaction = await prisma.transactions.findFirst({
      where: {
        id: transactionId,
      },
    });
    if (transaction?.status === TransactionStates.COMPLETE) {
      return { errorData: 'Transacción completada' };
    }
    if (!transaction) {
      return { errorData: errors.requestFailed };
    }
    const updatedTransaction = await prisma.transactions.update({
      where: {
        id: transactionId,
      },
      data: {
        status: TransactionStates.CANCELLED,
        description: 'Cancelado por usuario',
      },
    });
    // await sendEmailCancelWithdrawl(updatedTransaction);
    return updatedTransaction;
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: cancelWithdrawl', method: 'POST', req: transactionId });
    return null;
  }
};

export const cancelTransactions = async (transactionId: number, type: number) => {
  try {
    const transaction = await prisma.transactions.findFirst({
      where: {
        id: transactionId,
      },
    });
    if (transaction?.status === TransactionStates.COMPLETE) {
      return { errorData: 'Transacción completada' };
    }
    if (!transaction) {
      return { errorData: errors.requestFailed };
    }
    const updatedTransaction = await prisma.transactions.update({
      where: {
        id: transactionId,
      },
      data: {
        status: type === TransactionTypes.WITHDRAWL ? TransactionStates.CANCELLED : TransactionStates.FINISHED,
        description: 'Cancelado por usuario',
      },
    });
    return updatedTransaction;
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: cancelTransactions', method: 'POST', req: transactionId });
    return null;
  }
};

export const getTransactionStatus = async (transactionId: number) => {
  try {
    const transaction = await prisma.transactions.findFirst({
      where: {
        id: transactionId,
      },
      select: {
        status: true,
        vouchers: {
          select: {
            status: true,
          },
        },
      },
    });
    return !transaction ? null : { trxStatus: transaction?.status, vouchersStatus: transaction?.vouchers[0]?.status };
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: getTransactionStatus', method: 'GET', req: transactionId });
    return null;
  }
};

export const cancelDeposit = async (transactionId: number) => {
  try {
    const transaction = await prisma.transactions.findFirst({
      where: {
        id: transactionId,
      },
      include: {
        vouchers: true,
      },
    });

    if (transaction?.status === TransactionStates.COMPLETE) {
      return { errorData: 'Transacción completada' };
    }
    if (!transaction) {
      return { errorData: errors.requestFailed };
    }
    if (transaction.gateway === Gateway.BANK || (transaction.gateway === Gateway.CASH && transaction.type === TransactionTypes.WITHDRAWL)) {
      const updatedTransaction = await prisma.transactions.update({
        where: {
          id: transactionId,
        },
        data: {
          status: TransactionStates.CANCELLED,
        },
      });

      if (transaction && transaction.vouchers && transaction.vouchers.length > 0) {
        await prisma.vouchers.update({
          where: {
            id: transaction.vouchers[0].id,
          },
          data: {
            status: 3,
            feedback: 'Cancelado por el usuario',
          },
        });
      }

      // await sendEmailCancelWithdrawl(updatedTransaction);
      return updatedTransaction;
    }

    return { errorData: 'Transacción no cancelable' };
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: cancelWithdrawl', method: 'POST', req: transactionId });
    return null;
  }
};

export const resendNewWithdrawalCode = async (transactionId: number) => {
  try {
    const transaction = await prisma.transactions.findFirst({
      where: {
        id: transactionId,
      },
    });

    if (!transaction) {
      return { errorData: errors.requestFailed };
    }

    const currentDate = new Date();
    const expirationDate = transaction.expiration_date ?? new Date();

    let code = transaction.withdrawal_id;

    if (currentDate > expirationDate) {
    // New code
      code = Math.floor(10000 + Math.random() * 90000).toString();

      const newExpirationDate = new Date();
      newExpirationDate.setDate(newExpirationDate.getDate() + 2);

      await prisma.transactions.update({
        where: {
          id: transactionId,
        },
        data: {
          withdrawal_id: code,
          expiration_date: newExpirationDate,
        },
      });
      return {
        expiration_date: newExpirationDate,
        code,
      };
    }
    return {
      expiration_date: expirationDate,
      code,
    };
  } catch (error) {
    await exception(error, { route: '/features/dashboard/utils/index.ts: resendNewWithdrawalCode', method: 'POST', req: transactionId });
    return { errorData: errors.requestFailed };
  }
};

export const arrayForm = (kycLevel:number) => {
  return [
    {
      disabled: kycLevel === UserKYCLevel.APPROVED,
      id: 'firstName',
      label: 'Primer Nombre',
      placeholder: 'Primer Nombre',
      type: 'text',
    },
    {
      disabled: kycLevel === UserKYCLevel.APPROVED,
      id: 'secondName',
      label: 'Segundo Nombre',
      placeholder: 'Segundo Nombre',
      type: 'text',
    },
    {
      disabled: kycLevel === UserKYCLevel.APPROVED,
      id: 'lastName',
      label: 'Apellidos',
      placeholder: 'Apellidos',
      type: 'text',
    },
    {
      disabled: false,
      id: 'nickName',
      label: 'Nickname',
      placeholder: 'Nickname',
      type: 'text',
    },
    {
      disabled: false,
      id: 'phone',
      label: 'Número de Teléfono',
      placeholder: 'Número de Teléfono',
      type: 'number',
    },
    {
      disabled: kycLevel === UserKYCLevel.APPROVED,
      id: 'birthDate',
      label: 'Fecha de Nacimiento',
      placeholder: 'Fecha de Nacimiento',
      type: 'date',
    },
    {
      disabled: false,
      id: 'address',
      label: 'Dirección Principal',
      placeholder: 'Dirección Principal',
      type: 'text',
    },
    {
      disabled: false,
      id: 'city',
      label: 'Ciudad/Región',
      placeholder: 'Ciudad/Región',
      type: 'text',
    },
    {
      disabled: true,
      id: 'country',
      label: 'País',
      placeholder: 'País',
      type: 'text',
    },
  ];
};

export const getProgressValue = (amount: number, max: number) => {
  return max ? Number(((amount / max) * 100).toFixed(0)) : 0;
};
